/**
 * BillingSection
 *
 * Account page section linking to the Stripe billing portal, where users can
 * view billing history and receipts, update payment methods, or cancel their
 * subscription. Rendered only for users with Stripe billing. Plan changes are
 * handled separately by PlanSection's pricing page link.
 */
import React, { useState } from 'react';
import { Receipt, ExternalLink } from 'lucide-react';
import { redirectToBillingPortal } from '../../../services/stripeService';

export default function BillingSection() {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [error, setError] = useState(null);

  const openBillingPortal = async () => {
    setIsRedirecting(true);
    setError(null);
    try {
      await redirectToBillingPortal(`${window.location.origin}/app/account`);
    } catch (err) {
      setError(err.message || 'Failed to open the billing portal. Please try again.');
      setIsRedirecting(false);
    }
  };

  return (
    <div className="mt-16 grid max-w-7xl grid-cols-1 gap-x-8 gap-y-4 md:grid-cols-[300px,1fr]">
      <div className="py-6 text-center md:text-left">
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">Billing</h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Receipts and payment details are handled securely through Stripe.
        </p>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 sm:p-6 dark:border-gray-800 dark:bg-gray-900">
        <button
          type="button"
          onClick={openBillingPortal}
          disabled={isRedirecting}
          className="flex w-full items-center gap-4 text-left disabled:opacity-50"
        >
          <Receipt className="h-5 w-5 flex-shrink-0 text-gray-400 dark:text-gray-500" />
          <span className="flex-1">
            <span className="block text-sm font-medium text-gray-900 dark:text-white">
              Billing history & receipts
            </span>
            <span className="mt-0.5 block text-sm text-gray-600 dark:text-gray-400">
              View and download receipts, update your payment method, or cancel your subscription.
            </span>
          </span>
          <ExternalLink className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
        </button>

        {isRedirecting && (
          <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">Opening the Stripe billing portal…</p>
        )}
        {error && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400">{error}</p>
        )}
      </div>
    </div>
  );
}
